/**
 * Centralized Form Registry
 * Single source of truth for all form configurations
 *
 * Adding a new form:
 * 1. Add entry here with department, signatures, and file requirements
 * 2. Add component import in helperFuncs.js
 * 3. That's it! No switch statement modifications needed
 */

export const FORM_CONFIG = {
  backPay: {
    department: 'payroll',
    requiresManagerSignature: true,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'BackPay',
  },
  bonus: {
    department: 'payroll',
    requiresManagerSignature: true,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Bonus',
  },
  hotel: {
    department: 'finance',
    requiresManagerSignature: false,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Hotel',
  },
  mileage: {
    department: 'payroll',
    requiresManagerSignature: false,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Mileage',
  },
  nonMnMileage: {
    department: 'finance',
    requiresManagerSignature: false,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Non MN Mileage',
  },
  newHire: {
    department: 'hr',
    requiresManagerSignature: false,
    requiresEmployeeSignature: false,
    requiresFiles: true,
    fileRequirements: ['idBadge', 'govIdFront', 'govIdRear', 'ssnCard'],
    displayName: 'NewHire',
  },
  perDiem: {
    department: 'finance',
    requiresManagerSignature: false,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Perdiem',
  },
  pto: {
    department: 'payroll',
    requiresManagerSignature: true,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Pto',
  },
  targetOrder: {
    department: 'ops',
    requiresManagerSignature: true,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Target Order',
  },
  termination: {
    department: 'hr',
    requiresManagerSignature: false,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Termination',
  },
  timeAdjustment: {
    department: 'payroll',
    requiresManagerSignature: true,
    requiresEmployeeSignature: true,
    requiresFiles: false,
    displayName: 'Time Adjustment',
  },
  uniform: {
    department: 'hr',
    requiresManagerSignature: true,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Uniform',
  },
  workTicket: {
    department: 'finance',
    requiresManagerSignature: false,
    requiresEmployeeSignature: false,
    requiresFiles: false,
    displayName: 'Work Ticket',
  },
};

/**
 * Get form configuration
 * @param {string} formName - The name of the form
 * @returns {object|null} Form configuration or null if not found
 */
export const getFormConfig = (formName) => {
  return FORM_CONFIG[formName] || null;
};

/**
 * Get department for a form
 * @param {string} formName - The name of the form
 * @returns {string|null} Department name or null
 */
export const getFormDepartment = (formName) => {
  return FORM_CONFIG[formName]?.department || null;
};

/**
 * Check if form requires manager signature
 * @param {string} formName - The name of the form
 * @returns {boolean}
 */
export const requiresManagerSignature = (formName) => {
  return FORM_CONFIG[formName]?.requiresManagerSignature || false;
};

/**
 * Check if form requires employee signature
 * @param {string} formName - The name of the form
 * @returns {boolean}
 */
export const requiresEmployeeSignature = (formName) => {
  return FORM_CONFIG[formName]?.requiresEmployeeSignature || false;
};

/**
 * Check if form requires file uploads
 * @param {string} formName - The name of the form
 * @returns {boolean}
 */
export const requiresFiles = (formName) => {
  return FORM_CONFIG[formName]?.requiresFiles || false;
};

/**
 * Get all form names
 * @returns {string[]} Array of form names
 */
export const getAllFormNames = () => {
  return Object.keys(FORM_CONFIG);
};

/**
 * Generate header links from form registry
 * @returns {array} Array of link objects
 */
export const generateHeaderLinks = () => {
  return Object.entries(FORM_CONFIG).map(([key, config]) => ({
    link: `/?formName=${key}`,
    title: config.displayName,
  }));
};
